import { inventoryFloorRefusal } from '../compile/inventory-facet';
// janus-engine/movement/fall-check.ts — Lane MV4 S2 (2026-09-28): the SHARED fall twin (jump, compound drag, push / pull).
//
// A pure prediction of check_and_apply_falling() (includes/functions/janus/encounter/check_and_apply_falling.php) + the
// preamble of apply_fall_damage_in_feet() for ONE creature standing where it has just been put, on a DEPTH-AUTHORED (FT3D)
// encounter. Callers save its landing and resolve damage through the shared browser fall lane.
//
// Order is PHP's: slaved (piloting / riding) → grappled (hand back: the airborne-grappler lookup) → flight → hover → climb
// (hand back: indoor cling, outdoor trunk cling, wall hold) → wall hold for a non-climber (hand back near any wall run / scene
// wall) → underwater swim → the authored floor band (± the size-tier grace) → corpse support (hand back when any candidate
// exists) → inventory-top support (inventory-support.ts, Force Move S1) → the landing (map_min_z / 0 with nothing below) → the fall.
import { fallingShapeSupportFloor, type RestShape } from '../inventory/floor-move.js';
import { creatureBaseSizeFt, inventorySupport } from './inventory-support.js';
import { carrierEntryDefinition } from './carrier-entry.js';
import { encounterCreatureField } from '../../shared/array-types.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const rows = (value: unknown): Row[] => Array.isArray(value) ? value.filter(v => record(v) !== null) as Row[] : [];
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();

export const SPEED = {
  walking: 'DF516479-F164-87FA-7BB8-6F268BF1DCE7', jumping: 'B96061AE-3369-7E7A-B6A2-C7C8FBF2A23F',
  swimming: 'ED9810A9-3456-4D6B-BC5C-A1BAF5952B98', climbing: 'EDCBE2A8-CD3D-43DC-9B92-84A634BE12AE',
  flying: 'F1D0A27A-43F1-4A4C-A5F8-ED9BD2D908D4', burrowing: 'ACFB413D-15A8-835B-C821-9E101B6C4F60',
  hovering: 'AE7D4EB3-DC36-BD47-978F-1DBCFE2B616A',
} as const;
export const UNDERWATER_ENVIRONMENT = '19BCE18F-E1DC-D359-48A0-3B789741E058';
/** apply_fall_damage_in_feet()'s carrier ('Take Fall Damage', free_ability) — shared browser falling-damage carrier. */
export const FALL_DAMAGE_ABILITY = 'BE42824F-46BE-EC80-AB7F-B823190AB6C8';
const DIES = [6, 8, 10, 12, 20];
const HOVER_OFFSET_FT = 2;
const CARRIERS = ['passive_ability', 'reaction_ability', 'free_ability', 'menta', 'curse', 'at_will_spell',
  'innate_spell_per_long_rest', 'spells_known', 'opportunity_attack_ability', 'feat'];

export interface FallReaders {
  value: (row: Row, name: string) => unknown;
  entries: (row: Row, name: string) => unknown[];
  definition: (id: string) => Row | null;
  statuses: (row: Row) => Row[];
  hasStatus: (row: Row, name: string) => boolean;
  /** The size-tier body (rulesDiameter) and the collision body (diameter / height: a mesh body when banked). */
  geometry: (row: Row) => { diameter: number; height: number; rulesDiameter?: number } | null;
  position: (row: Row) => [number, number, number];
}

export type FallDamage = { dice: number; die: number };
export type FallVerdict =
  /** No fall. `snapZ`: the micro-snap PHP persists (|Δ| > 0.05 ft inside the grace), else the row stays where it is. */
  | { kind: 'none'; why: 'slaved' | 'held' | 'flight' | 'swim' | 'floor' | 'supported'; snapZ?: number }
  /** A fall of `fallFt` to `landingZ`. `damage` null = no dice (≤ 5 ft after negation, < 10 ft, or hover-reduced to 0);
   *  `prone` = PHP applies Prone (effective ≥ 10 ft); `negate` the negation it subtracted ('inf' = none of it lands). */
  | { kind: 'fall'; fallFt: number; landingZ: number; damage: FallDamage | null; prone: boolean; negate: number | 'inf'; logged: boolean }
  | { kind: 'handback'; reason: string };

const hand = (reason: string): FallVerdict => ({ kind: 'handback', reason: 'fall:' + reason });
const truthy = (v: unknown): boolean => { const x = scalar(v); return x !== undefined && x !== null && x !== '' && x !== 0 && x !== '0' && x !== false; };

/** A definition (or row) carries a layer with a value. */
function carries(read: FallReaders, def: Row, layer: string): boolean {
  const v = read.value(def, layer);
  return (v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)) || read.entries(def, layer).length > 0;
}
function definitionOf(read: FallReaders, entry: unknown): Row | null {
  return carrierEntryDefinition(entry, read.definition).definition;
}

/** resolve_negate_falling_damage_in_feet(): the largest single negation, 'inf' = total. null = a source this twin cannot judge. */
export function fallNegation(row: Row, read: FallReaders): number | 'inf' | null {
  let best = 0;
  const scan = (raw: unknown): 'inf' | void => {
    for (const entry of Array.isArray(raw) ? raw : [raw]) {
      if (entry === null || entry === undefined || entry === '') continue;
      if (String(entry).trim().toLowerCase() === 'inf') return 'inf';
      const n = Number(entry);
      if (Number.isFinite(n) && n > best) best = n;
    }
  };
  if (scan(read.value(row, 'negate_falling_damage_in_feet')) === 'inf') return 'inf';
  if (scan(read.entries(row, 'negate_falling_damage_in_feet')) === 'inf') return 'inf';
  // A passive's negation is judged by its runtime conditions at the moment of the fall (check_ability_conditions) — PHP's.
  for (const entry of read.entries(row, 'passive_ability')) {
    const def = definitionOf(read, entry);
    if (!def) return null;
    if (carries(read, def, 'negate_falling_damage_in_feet')) return null;
  }
  for (const status of read.statuses(row)) {
    const def = definitionOf(read, status);
    if (!def) return null;
    if (scan(read.entries(def, 'negate_falling_damage_in_feet')) === 'inf') return 'inf';
    if (scan(read.value(def, 'negate_falling_damage_in_feet')) === 'inf') return 'inf';
  }
  return best;
}

/** apply_fall_damage_in_feet(): Prone at ≥ 10 ft effective, floor(eff / 10) dice (die by count, capped at 5), hovering −2 dice. */
export function fallDamageTier(fallFt: number, negate: number | 'inf', hovering: boolean): { damage: FallDamage | null; prone: boolean } {
  if (negate === 'inf') return { damage: null, prone: false };
  const effective = negate > 0 ? Math.max(0, fallFt - negate) : fallFt;
  if (effective <= 5 || effective < 10) return { damage: null, prone: false };
  const n = Math.floor(effective / 10);
  const dice = n - (hovering ? 2 : 0);
  return { damage: dice > 0 ? { dice, die: DIES[Math.min(n - 1, 4)]! } : null, prone: true };
}

/** Does this row carry a begins-falling trigger (janus_trigger 'triggers_when_caster_begins_falling', self-only)? null = unknown. */
function beginsFallingTrigger(row: Row, read: FallReaders): boolean | null {
  const entries: unknown[] = [...read.statuses(row)];
  for (const carrier of CARRIERS) entries.push(...read.entries(row, carrier), ...rows(row[carrier]));
  for (const entry of entries) {
    const def = definitionOf(read, entry);
    if (!def) return null;
    if (carries(read, def, 'triggers_when_caster_begins_falling')) return true;
  }
  return false;
}

/** Could ft3d_wall_hold_face() find a face at (x, y)? Any wall run (inventory_generation.wallRun) or DAFT3D scene wall whose face lies
 *  within the standoff (+ 1 ft) across its axis and whose span (± 1 ft) covers the body. Height and openings are ignored (they only
 *  ever REMOVE a hold). A row that names a wall run the page cannot read counts. */
function wallHoldCandidate(inventories: Row[], shape: RestShape, x: number, y: number, standoff: number): boolean {
  const near = (axis: unknown, at: unknown, span: unknown, reach: number): boolean | null => {
    const s = record(span), a = Number(at), lo = Number(s?.min), hi = Number(s?.max);
    if ((axis !== 'x' && axis !== 'y') || ![a, lo, hi].every(Number.isFinite)) return null;
    const [across, along] = axis === 'x' ? [x, y] : [y, x];
    return Math.abs(across - a) <= reach + 1 && along >= lo - 1 && along <= hi + 1;
  };
  for (const inv of inventories) {
    let generation: unknown = record(inv.inventory)?.inventory_generation ?? inv.inventory_generation;
    if (typeof generation === 'string') {
      if (!generation.includes('wallRun')) continue;
      try { generation = JSON.parse(generation); } catch { return true; }
    }
    const run = record(record(generation)?.wallRun);
    if (!run) { if (JSON.stringify(generation ?? '').includes('wallRun')) return true; continue; }
    const hit = near(run.axis, run.at, run.span, standoff);
    if (hit !== false) return true;
  }
  for (const wall of Array.isArray(shape.sceneWalls) ? shape.sceneWalls : []) {
    const w = record(wall);
    const hit = w ? near(w.axis, w.at, w.span, Math.max(0.05, Number(w.thicknessFt ?? 0.5) || 0.5) / 2 + standoff) : null;
    if (hit !== false) return true;
  }
  return false;
}

export interface FallInput {
  /** The creature, as the encounter holds it; `at` overrides its position (the landing a move proposes). */
  row: Row;
  at?: { x: number; y: number; z: number };
  encounter: Row;
  shape: RestShape | null;
  /** check_and_apply_falling($resolve_pilot_support): an explicit FT3D force move resolves a pilot's own support. */
  resolvePilotSupport?: boolean;
  /** The row's grappler when the caller KNOWS it (the grapple drag: the dragging creature). PHP holds a grappled row aloft
   *  while that grappler flies / hovers (and has the speed for it); without it a grappled row hands back. `null` = the caller resolved
   *  it and PHP's lookup finds no grappler row (resolve_regress_grapple_escape_grappler_and_status_row → not in the encounter): the
   *  ordinary fall logic runs (Session A2 0b — a painted wall's zone grapple). */
  grappler?: Row | null;
  /** Status removal also serves legacy scenes, whose persisted Z is the creature's centre. */
  legacyCentre?: boolean;
  /** A powerless underwater swimmer settles on support without falling damage or fall triggers. */
  underwaterSink?: boolean;
}

export function predictFall(input: FallInput, read: FallReaders): FallVerdict {
  const { row, encounter, shape } = input;
  const floorRefusal = inventoryFloorRefusal(encounter);
  if (floorRefusal) return hand(floorRefusal);
  const ec = record(row.encounter_creatures) ?? {};
  const selfId = upper(encounterCreatureField(ec, 'encounter_creature_id'));
  if ((!input.resolvePilotSupport && read.hasStatus(row, 'piloting')) || read.hasStatus(row, 'riding')) return { kind: 'none', why: 'slaved' };
  // An airborne grappler holds the row up — PHP looks the grappler up through the grapple status row.
  if (read.hasStatus(row, 'grappled') && input.grappler === undefined) return hand('grappled');
  if (read.hasStatus(row, 'grappled') && input.grappler) {
    const g = input.grappler, gp = upper(encounterCreatureField(record(g.encounter_creatures), 'preferred_speed_type'));
    const gs = (name: string): number => Math.trunc(Number(scalar(read.value(g, name)) ?? 0) || 0);
    if ((gp === SPEED.flying && gs('speed_flying') > 0) || (gp === SPEED.hovering && gs('speed_hovering') > 0)) return { kind: 'none', why: 'held' };
  }
  const incapacitated = read.hasStatus(row, 'unconscious') || read.hasStatus(row, 'paralyzed');
  const preferred = upper(encounterCreatureField(ec, 'preferred_speed_type'));
  const speed = (name: string): number => Math.trunc(Number(scalar(read.value(row, name)) ?? 0) || 0);
  if (!incapacitated && preferred === SPEED.flying && speed('speed_flying') > 0) return { kind: 'none', why: 'flight' };
  const hovering = !incapacitated && preferred === SPEED.hovering && speed('speed_hovering') > 0;
  const hoverOffset = hovering ? HOVER_OFFSET_FT : 0;
  // Climbers: indoor cling, outdoor trunk cling and the wall hold are the wall-climb subsystem's (§1a — deferred).
  if (!incapacitated && preferred === SPEED.climbing) return hand('climber');
  const authored = !!shape && Array.isArray(shape.boxes);
  if (!authored && !input.legacyCentre) return hand('legacy-shape');
  const [px, py, pz] = input.at ? [input.at.x, input.at.y, input.at.z] : read.position(row);
  if (![px, py, pz].every(Number.isFinite)) return hand('position');
  const body = read.geometry(row);
  if (!body || !(body.height > 0) || !(body.diameter > 0)) return hand('body');
  const grace = Math.max(0, body.rulesDiameter ?? body.diameter);
  // The compiler's legacy physics height includes the selected portrait aspect; the FT3D collision body does not.
  const height = authored ? body.height : Number(scalar(read.value(row, 'creature_physics_height')));
  if (!Number.isFinite(height) || height <= 0) return hand('legacy-height');
  const centreOffset = authored ? 0 : height / 2, bottom = pz - centreOffset;
  const floor = authored ? (!shape!.boxes.length && pz + grace >= 0 ? 0 : fallingShapeSupportFloor(shape!, px, py, pz, grace)) : null;
  // A non-climber gripping a wall face (ft3d_wall_hold_face: a walk-derived climber with free hands, OFF the floor band —
  // within 0.05 ft of it the hold is impossible) — any wall-run row or scene wall could be one; the geometry is the deferred
  // wall-climb subsystem's.
  const offFloor = floor === null || Math.abs(pz - floor) > 0.05;
  // Force Move S2: only a wall face the body could be GRIPPING — ft3d_wall_hold_face needs the body at its exact standoff
  // (collision radius + 0.35 ft, ± 0.05) off a run / scene-wall face, within its span — so a face farther than that bound (+ 1 ft
  // of slack) cannot hold it. An unreadable run is still a candidate.
  // ft3d_wall_hold_face walks EVERY inventory row, deleted ones too (Codex S2 review) — so does the candidate scan.
  if (authored && offFloor && !incapacitated && wallHoldCandidate(rows(encounter.encounter_inventories_arr), shape!, px, py, body.diameter / 2 + 0.35)) return hand('wall-hold-candidate');
  const env = upper(scalar(record(encounter.encounters)?.encounter_environment ?? encounter.encounter_environment ?? read.value(encounter, 'location_environment')));
  if (!input.underwaterSink && preferred === SPEED.swimming && env === UNDERWATER_ENVIRONMENT) return { kind: 'none', why: 'swim' };
  if (!authored && !hovering && bottom <= grace)
    return bottom > 0.1 ? { kind: 'none', why: 'floor', snapZ: centreOffset } : { kind: 'none', why: 'floor' };
  // Corpse tops (ft3d_corpse_support_floor) are support too — not modelled. PHP only counts a corpse whose collision square
  // CONTAINS the mover's whole square (|Δx| + radius ≤ half on both axes), so only a corpse whose square (grown by 1 ft of slack)
  // reaches the landing is a candidate — any candidate hands back, as does a corpse whose body the page cannot measure.
  for (const other of authored ? rows(encounter.encounter_creatures_arr) : []) {
    const oec = record(other.encounter_creatures) ?? {};
    if (upper(oec.encounter_creature_id) === selfId || Number(scalar(oec.encounter_creature_private) ?? 0) === 1) continue;
    if (Number(scalar(oec.encounter_creature_dead) ?? 0) !== 1) continue;
    const corpse = read.geometry(other);
    const [cx, cy] = read.position(other);
    const half = corpse ? Math.max(corpse.diameter, corpse.rulesDiameter ?? 0) / 2 + 1 : NaN;
    if (!Number.isFinite(half) || ![cx, cy].every(Number.isFinite)) return hand('corpse-support');
    if (Math.abs(px - cx) <= half && Math.abs(py - cy) <= half) return hand('corpse-support');
  }
  if (!hovering && floor !== null && Math.abs(pz - floor) <= grace) {
    return Math.abs(pz - floor) > 0.05 ? { kind: 'none', why: 'floor', snapZ: floor } : { kind: 'none', why: 'floor' };
  }
  // Inventory tops (step 5): PHP's x-only overlap against the ART height, twinned in inventory-support.ts (Force Move S1 —
  // oracle parity on 1 944 real rows / 720 sweeps). PHP's sweep walks EVERY row (deleted ones too), so this does as well.
  const support = inventorySupport({ inventories: rows(encounter.encounter_inventories_arr), moverX: px,
    moverBase: creatureBaseSizeFt(row, read), bottom, grace, authored }, read);
  if ('unknown' in support) return hand(`inventory-support:${support.unknown}`);
  const highest = support.top !== null && (floor === null || support.top > floor) ? support.top : floor;
  // Nothing under the feet: PHP lands on the encounter's min_z_pos below ground, else z = 0.
  const landingSurface = highest !== null ? highest
    : (bottom < -0.05 || pz < -0.05 ? (Number(scalar(read.value(encounter, 'min_z_pos'))) || 0) - centreOffset : 0);
  const landing = landingSurface + hoverOffset;
  if (landing >= bottom - grace) {
    return bottom > landing + 0.05 ? { kind: 'none', why: 'supported', snapZ: landing + centreOffset } : { kind: 'none', why: 'supported' };
  }
  const fallFt = bottom - landing;
  if (input.underwaterSink) return { kind: 'fall', fallFt, landingZ: landing + centreOffset, damage: null, prone: false, negate: 0, logged: fallFt > 0.05 && selfId !== '' };
  const trigger = beginsFallingTrigger(row, read);
  if (trigger !== false) return hand(trigger === null ? 'begins-falling-definition' : 'begins-falling-trigger');
  const negate = fallNegation(row, read);
  if (negate === null) return hand('negate-source');
  // The hover dice reduction has NO incapacitation check (apply_fall_damage_in_feet: preferred hovering + speed_hovering > 0) —
  // only the 2 ft landing offset needs an active hover (Codex S2 F8).
  const tier = fallDamageTier(fallFt, negate, preferred === SPEED.hovering && speed('speed_hovering') > 0);
  return { kind: 'fall', fallFt, landingZ: landing + centreOffset, damage: tier.damage, prone: tier.prone, negate, logged: fallFt > 0.05 && selfId !== '' };
}

/** Graceful sinking shares physical support geometry, but never the fall/damage pipeline. */
export function predictSink(input: FallInput, read: FallReaders):
  Exclude<FallVerdict, { kind: 'fall' }> | { kind: 'sink'; sinkFt: number; landingZ: number } {
  const result = predictFall({ ...input, underwaterSink: true }, read);
  return result.kind === 'fall' ? { kind: 'sink', sinkFt: result.fallFt, landingZ: result.landingZ } : result;
}
